import React, { useState } from 'react';

import { DeleteCustomersModal } from '@/sections/customerManagement/components/modals/DeleteCustomersModal';
import { AISpendCard } from '@/sections/settings/components/cards/AISpendCard';
import { AllowedDomainsSection } from '@/sections/settings/components/cards/AllowedDomainsSection';
import { ComplianceFrameworksSection } from '@/sections/settings/components/cards/ComplianceFrameworksSection';
import { OrgProfileSection } from '@/sections/settings/components/cards/OrgProfileSection';
import { PerScanAIBudgetCard } from '@/sections/settings/components/cards/PerScanAIBudgetCard';
import { PraetorianFeaturesSection } from '@/sections/settings/components/cards/PraetorianFeaturesSection';
import { SSOProviderSection } from '@/sections/settings/components/cards/SSOProviderSection';
import {
  aiModuleOptions,
  knossosModuleOptions,
  staticIpModuleOptions,
} from '@/sections/settings/components/sections/chariotModuleOptions';
import { useAuth } from '@/state/auth';

interface OrganizationSettingsCardsProps {
  email?: string;
  editable?: boolean;
}

export const OrganizationSettingsCards: React.FC<
  OrganizationSettingsCardsProps
> = ({ email, editable = true }) => {
  const { me, isPraetorianUser } = useAuth();

  // Tenant deletion (Praetorian only) — routes to the canonical
  // DeleteCustomersModal → POST /account/deletion flow.
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // The delete request targets the impersonated customer (`email`). With no
  // impersonation target the request would hit the operator's own
  // @praetorian.com tenant, which the backend denylist always refuses — so the
  // affordance is disabled with an explanatory tooltip instead.
  const targetEmail = email ?? me;
  const deleteDisabledReason = email
    ? undefined
    : 'Switch into the customer’s tenant to request deletion';

  const showPraetorianFeatures =
    isPraetorianUser || me === 'admin@praetorian.com';

  return (
    <>
      <div
        className="flex flex-col gap-4"
        data-testid="organization-settings-card"
      >
        {/* Account Section */}
        <div
          className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6"
          data-testid="account-section-header"
          data-tutorial="org-account-section"
        >
          <div className="mb-4 flex items-start gap-3">
            <div className="bg-chariot-brand-tertiary flex h-10 w-10 items-center justify-center rounded-lg">
              <svg
                className="text-chariot-brand-primary h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Account</h3>
              <p className="text-chariot-text-secondary mt-0.5 text-sm">
                Organization profile, authentication, and account settings
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <OrgProfileSection email={email} editable={editable} />
            <SSOProviderSection email={email} editable={editable} />
            {showPraetorianFeatures && (
              <AllowedDomainsSection email={email} editable={editable} />
            )}
          </div>
        </div>

        {/* Security Frameworks and Standards Section */}
        <div
          className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6"
          data-testid="frameworks-section-header"
          data-tutorial="frameworks-section"
        >
          <div className="mb-4 flex items-start gap-3">
            <div className="bg-chariot-brand-tertiary flex h-10 w-10 items-center justify-center rounded-lg">
              <svg
                className="text-chariot-brand-primary h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                Security Frameworks and Standards
              </h3>
              <p className="text-chariot-text-secondary mt-0.5 text-sm">
                Enable compliance framework mappings for risk assessments
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <ComplianceFrameworksSection email={email} editable={editable} />
          </div>
        </div>

        {/* Static IP Addresses (standalone)
            Always rendered so customers can SEE the toggle (read-only),
            directly below Allowed Email Domains. Praetorian users may
            manage it; customers see it disabled with a "Managed by
            Praetorian" tooltip reflecting its current state. */}
        <PraetorianFeaturesSection
          email={email}
          editable={editable}
          options={staticIpModuleOptions}
          canToggleFlags={showPraetorianFeatures}
          showDeleteAccount={false}
        />

        {/* Deception Environments (Knossos)
            Same pattern as Static IP and AI Features — always rendered
            so customers see the toggle (read-only); Praetorian users
            may manage it. */}
        <div data-tutorial="deception-section" className="flex flex-col gap-4">
          <PraetorianFeaturesSection
            email={email}
            editable={editable}
            options={knossosModuleOptions}
            header="Deception"
            canToggleFlags={showPraetorianFeatures}
            showDeleteAccount={false}
          />
        </div>

        {/* AI Features Section
            Always rendered so customers can SEE the AI feature toggles
            (read-only). Praetorian users may manage the flags; customers
            see each toggle disabled with a "Managed by Praetorian"
            tooltip reflecting its current state. The Delete Account
            danger card stays Praetorian-only via showDeleteAccount.

            AI Spend is slotted as the first card under the "AI Features"
            header (visible to all users; the card renders the marked-up
            budget/spend values verbatim from the GET hook, with editing
            gated to Praetorians inside AISpendCard). The per-scan AI budget
            card sits directly beneath it (moved here from the Scanning tab);
            editing is likewise Praetorian-gated inside the card. */}
        <PraetorianFeaturesSection
          email={email}
          editable={editable}
          options={aiModuleOptions}
          header="AI Features"
          cardsAfterHeader={
            <>
              <AISpendCard email={email} />
              <PerScanAIBudgetCard email={email} editable={editable} />
            </>
          }
          canToggleFlags={showPraetorianFeatures}
          showDeleteAccount={showPraetorianFeatures}
          onDeleteAccount={() => setIsDeleteModalOpen(true)}
          deleteDisabledReason={deleteDisabledReason}
        />
      </div>

      {/* Tenant deletion (Praetorian-only; backend enforces SuperAdmin + denylist) */}
      <DeleteCustomersModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        customers={[{ email: targetEmail, displayName: targetEmail }]}
        onSuccess={() => setIsDeleteModalOpen(false)}
      />
    </>
  );
};
